import React, { useState, useEffect } from 'react';
import { X, Calculator, TrendingUp, DollarSign, Clock, Share2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';

// ==================== EDITABLE CONSTANTS ====================
const CONSTANTS = {
  plans: {
    starter: { name: 'Starter', price: 28 },
    pro: { name: 'Professional', price: 48 }
  },
  defaults: {
    technicians: 8,
    workOrdersPerWeek: 60,
    plannerHoursPerWeek: 6,
    technicianHourlyCost: 38,
    downtimeHoursPerMonth: 10,
    downtimeCostPerHour: 1200,
    currentToolCost: 0,
    selectedPlan: 'starter',
    timeSavedPct: 40,
    downtimeReductionPct: 10,
    techAdminMinPerWeek: 10
  },
  ranges: {
    timeSavedPct: { min: 20, max: 60 },
    downtimeReductionPct: { min: 5, max: 25 },
    techAdminMinPerWeek: { min: 5, max: 20 }
  }
};

// ==================== UTILITY FUNCTIONS ====================
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-CA').format(Math.round(value));
};

// ==================== CALCULATION FUNCTIONS ====================
const calculateROI = (inputs) => {
  const {
    technicians,
    workOrdersPerWeek,
    plannerHoursPerWeek,
    technicianHourlyCost,
    downtimeHoursPerMonth,
    downtimeCostPerHour,
    currentToolCost,
    selectedPlan,
    timeSavedPct,
    downtimeReductionPct,
    techAdminMinPerWeek
  } = inputs;

  const composysPlanPrice = CONSTANTS.plans[selectedPlan].price;

  // Calculate planner time savings
  const plannerTimeSavedHr = plannerHoursPerWeek * (timeSavedPct / 100);
  const plannerSavingsMonth = plannerTimeSavedHr * technicianHourlyCost * 4.33;

  // Calculate technician admin time savings
  const techAdminSavedHrMonth = technicians * (techAdminMinPerWeek / 60) * 4.33;
  const techAdminSavingsMonth = techAdminSavedHrMonth * technicianHourlyCost;

  // Calculate downtime savings
  const downtimeSavedHrMonth = downtimeHoursPerMonth * (downtimeReductionPct / 100);
  const downtimeSavingsMonth = downtimeSavedHrMonth * downtimeCostPerHour;

  // Calculate totals
  const grossSavingsMonth = plannerSavingsMonth + techAdminSavingsMonth + downtimeSavingsMonth;
  const netSavingsMonth = grossSavingsMonth - (composysPlanPrice - currentToolCost);
  const roiPct = (netSavingsMonth / (composysPlanPrice || 1)) * 100;
  const paybackDays = 30 * (composysPlanPrice / Math.max(netSavingsMonth, 1));

  return {
    plannerSavingsMonth,
    techAdminSavingsMonth,
    downtimeSavingsMonth,
    grossSavingsMonth,
    netSavingsMonth,
    roiPct,
    paybackDays
  };
};

// ==================== EVENT TRACKING ====================
const trackEvent = (eventName, payload) => {
  console.log(`[ROI Calculator Event] ${eventName}:`, payload);
  
  // Push to dataLayer if available (for Google Tag Manager)
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...payload
    });
  }
};

// ==================== MAIN COMPONENT ====================
const ROICalculator = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  
  // State for inputs
  const [inputs, setInputs] = useState(() => {
    // Try to load from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('roi_calculator_inputs');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved inputs:', e);
        }
      }
    }
    return { ...CONSTANTS.defaults };
  });

  // State for results
  const [results, setResults] = useState(null);

  // Save to localStorage whenever inputs change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('roi_calculator_inputs', JSON.stringify(inputs));
    }
  }, [inputs]);

  // Calculate ROI whenever inputs change
  useEffect(() => {
    const calculatedResults = calculateROI(inputs);
    setResults(calculatedResults);
  }, [inputs]);

  // Track calculation event
  useEffect(() => {
    if (results && isOpen) {
      trackEvent('roi_calculated', {
        inputs,
        results
      });
    }
  }, [results, isOpen]);

  // Handle input change
  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle reset
  const handleReset = () => {
    setInputs({ ...CONSTANTS.defaults });
    if (typeof window !== 'undefined') {
      localStorage.removeItem('roi_calculator_inputs');
    }
  };

  // Handle share
  const handleShare = () => {
    const params = new URLSearchParams(inputs);
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    
    navigator.clipboard.writeText(url).then(() => {
      alert('Link copied to clipboard! Share this estimate with your team.');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy link. Please copy manually: ' + url);
    });
  };

  // Handle CTA clicks
  const handleTrialClick = () => {
    trackEvent('cta_trial_clicked', { inputs, results });
    // User is already on login page, so just close the modal
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
          aria-label="Close calculator"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-t-2xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
              <Calculator className="w-6 h-6" />
            </div>
            <h2 className="text-3xl font-bold">{t('roiCalculatorTitle')}</h2>
          </div>
          <p className="text-blue-100 text-lg">
            {t('roiCalculatorSubtitle')}
          </p>
          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>{t('noCreditCard')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>{t('fastSetup')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>{t('builtForIndustrial')}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column - Inputs */}
            <div className="space-y-6">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-lg">{t('yourCurrentOperation')}</CardTitle>
                  <CardDescription>{t('enterTeamDetails')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="technicians">{t('numberOfTechnicians')}</Label>
                    <Input
                      id="technicians"
                      type="number"
                      min="1"
                      max="1000"
                      value={inputs.technicians}
                      onChange={(e) => handleInputChange('technicians', parseInt(e.target.value) || 0)}
                      className="font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="workOrders">{t('workOrdersPerWeek')}</Label>
                    <Input
                      id="workOrders"
                      type="number"
                      min="1"
                      max="10000"
                      value={inputs.workOrdersPerWeek}
                      onChange={(e) => handleInputChange('workOrdersPerWeek', parseInt(e.target.value) || 0)}
                      className="font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plannerHours">{t('plannerTimeWeekly')}</Label>
                    <Input
                      id="plannerHours"
                      type="number"
                      min="0"
                      max="168"
                      value={inputs.plannerHoursPerWeek}
                      onChange={(e) => handleInputChange('plannerHoursPerWeek', parseFloat(e.target.value) || 0)}
                      className="font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hourlyCost">{t('avgTechnicianCost')}</Label>
                    <Input
                      id="hourlyCost"
                      type="number"
                      min="0"
                      max="1000"
                      value={inputs.technicianHourlyCost}
                      onChange={(e) => handleInputChange('technicianHourlyCost', parseFloat(e.target.value) || 0)}
                      className="font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="downtime">{t('estimatedDowntime')}</Label>
                    <Input
                      id="downtime"
                      type="number"
                      min="0"
                      max="720"
                      value={inputs.downtimeHoursPerMonth}
                      onChange={(e) => handleInputChange('downtimeHoursPerMonth', parseFloat(e.target.value) || 0)}
                      className="font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="downtimeCost">{t('downtimeCostPerHour')}</Label>
                    <Input
                      id="downtimeCost"
                      type="number"
                      min="0"
                      max="100000"
                      value={inputs.downtimeCostPerHour}
                      onChange={(e) => handleInputChange('downtimeCostPerHour', parseFloat(e.target.value) || 0)}
                      className="font-medium"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currentTool">{t('currentToolCost')}</Label>
                    <Input
                      id="currentTool"
                      type="number"
                      min="0"
                      max="10000"
                      value={inputs.currentToolCost}
                      onChange={(e) => handleInputChange('currentToolCost', parseFloat(e.target.value) || 0)}
                      className="font-medium"
                    />
                    <p className="text-xs text-gray-500">{t('leaveAtZero')}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="plan">{t('composysMaintProPlan')}</Label>
                    <Select
                      value={inputs.selectedPlan}
                      onValueChange={(value) => handleInputChange('selectedPlan', value)}
                    >
                      <SelectTrigger id="plan">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">
                          {t('starterPlan')} - ${CONSTANTS.plans.starter.price}/month
                        </SelectItem>
                        <SelectItem value="pro">
                          {t('professionalPlan')} - ${CONSTANTS.plans.pro.price}/month
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Assumptions */}
              <Card className="border-2 border-blue-100 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="text-lg">{t('assumptionsAdjustable')}</CardTitle>
                  <CardDescription>{t('fineTuneEstimates')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="timeSaved">{t('timeSavedPlanning')}</Label>
                      <span className="text-sm font-semibold text-blue-600">{inputs.timeSavedPct}%</span>
                    </div>
                    <input
                      id="timeSaved"
                      type="range"
                      min={CONSTANTS.ranges.timeSavedPct.min}
                      max={CONSTANTS.ranges.timeSavedPct.max}
                      value={inputs.timeSavedPct}
                      onChange={(e) => handleInputChange('timeSavedPct', parseInt(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{CONSTANTS.ranges.timeSavedPct.min}%</span>
                      <span>{CONSTANTS.ranges.timeSavedPct.max}%</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="downtimeReduction">{t('downtimeReduction')}</Label>
                      <span className="text-sm font-semibold text-blue-600">{inputs.downtimeReductionPct}%</span>
                    </div>
                    <input
                      id="downtimeReduction"
                      type="range"
                      min={CONSTANTS.ranges.downtimeReductionPct.min}
                      max={CONSTANTS.ranges.downtimeReductionPct.max}
                      value={inputs.downtimeReductionPct}
                      onChange={(e) => handleInputChange('downtimeReductionPct', parseInt(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{CONSTANTS.ranges.downtimeReductionPct.min}%</span>
                      <span>{CONSTANTS.ranges.downtimeReductionPct.max}%</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="adminTime">{t('adminTimeSaved')}</Label>
                      <span className="text-sm font-semibold text-blue-600">{inputs.techAdminMinPerWeek} min</span>
                    </div>
                    <input
                      id="adminTime"
                      type="range"
                      min={CONSTANTS.ranges.techAdminMinPerWeek.min}
                      max={CONSTANTS.ranges.techAdminMinPerWeek.max}
                      value={inputs.techAdminMinPerWeek}
                      onChange={(e) => handleInputChange('techAdminMinPerWeek', parseInt(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{CONSTANTS.ranges.techAdminMinPerWeek.min} min</span>
                      <span>{CONSTANTS.ranges.techAdminMinPerWeek.max} min</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Results */}
            <div className="space-y-6">
              {results && (
                <>
                  {/* Main Results Cards */}
                  <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex items-center gap-3 pb-4 border-b border-green-200">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">{t('estimatedMonthlySavings')}</p>
                          <p className="text-3xl font-bold text-green-700">
                            {formatCurrency(results.grossSavingsMonth)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 pb-4 border-b border-green-200">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <DollarSign className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">{t('netSavingsAfterSub')}</p>
                          <p className="text-3xl font-bold text-blue-700">
                            {formatCurrency(results.netSavingsMonth)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {t('perMonth')}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-white rounded-lg border border-green-200">
                          <p className="text-sm text-gray-600 mb-1">{t('roi')}</p>
                          <p className="text-2xl font-bold text-green-700">
                            {formatNumber(results.roiPct)}%
                          </p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-green-200">
                          <p className="text-sm text-gray-600 mb-1">{t('paybackPeriod')}</p>
                          <p className="text-2xl font-bold text-green-700">
                            {Math.round(results.paybackDays)} {t('days')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">{t('savingsBreakdown')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium">{t('plannerTimeSavings')}</span>
                        </div>
                        <span className="font-semibold text-blue-700">
                          {formatCurrency(results.plannerSavingsMonth)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium">{t('technicianAdminSavings')}</span>
                        </div>
                        <span className="font-semibold text-purple-700">
                          {formatCurrency(results.techAdminSavingsMonth)}
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-orange-600" />
                          <span className="text-sm font-medium">{t('downtimeSavings')}</span>
                        </div>
                        <span className="font-semibold text-orange-700">
                          {formatCurrency(results.downtimeSavingsMonth)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* CTAs */}
                  <div className="space-y-3">
                    <Button
                      onClick={handleTrialClick}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-6 text-lg shadow-lg"
                    >
                      {t('start14DayTrial')}
                    </Button>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleShare}
                        variant="outline"
                        className="flex-1"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        {t('shareEstimate')}
                      </Button>
                      <Button
                        onClick={handleReset}
                        variant="outline"
                        className="flex-1"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        {t('reset')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              {t('composysFooter')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculator;
